import React, { useState, useEffect } from 'react';
    import { motion } from 'framer-motion';
    import { assetsApi } from '../../lib/assetsApi';
    import { useNotification } from '../../contexts/NotificationContext';
    import SafeIcon from '../../common/SafeIcon';
    import AssetSelector from './AssetSelector';
    import * as FiIcons from 'react-icons/fi';

    const { FiSave, FiX, FiUpload, FiTrash2, FiPlus, FiImage, FiLock } = FiIcons;

    const LOCKED_COLORS = {
      primary: '#03245E',
      accent: '#E2C164',
      background: '#F3F7FA',
      secondary: '#D7B740',
      highlight: '#F7EFE2'
    };
    const LOCKED_FONTS = {
      titles: 'Anton',
      body: 'Helvetica World',
      subtitle: 'Montserrat',
      heading: 'League Spartan',
      highlight: 'Gistesy'
    };

    export default function BrandKitForm({ initialData, onSubmit, onCancel }) {
      const { showError } = useNotification();
      const [formData, setFormData] = useState({
        id: null,
        name: '',
        colors: LOCKED_COLORS,
        fonts: LOCKED_FONTS,
        watermark_settings: { opacity: 0.3, position: 'bottom-right', size: 'medium' },
        default_margins: { top: 20, right: 20, bottom: 20, left: 20 },
        logos: [],
        watermarks: [],
        selected_logo_id: null,
        selected_watermark_id: null
      });
      const [uploadingLogos, setUploadingLogos] = useState(false);
      const [uploadingWatermarks, setUploadingWatermarks] = useState(false);
      const [selectedLogo, setSelectedLogo] = useState(null);
      const [selectedWatermark, setSelectedWatermark] = useState(null);

      useEffect(() => {
        if (initialData) {
          console.log('Loading brand kit data:', initialData);
          setFormData({
            id: initialData.id || null,
            name: initialData.name || '',
            colors: initialData.colors || LOCKED_COLORS,
            fonts: initialData.fonts || LOCKED_FONTS,
            watermark_settings: initialData.watermark_settings || { opacity: 0.3, position: 'bottom-right', size: 'medium' },
            default_margins: initialData.default_margins || { top: 20, right: 20, bottom: 20, left: 20 },
            logos: initialData.logos || [],
            watermarks: initialData.watermarks || [],
            selected_logo_id: initialData.selected_logo_id || null,
            selected_watermark_id: initialData.selected_watermark_id || null
          });

          if (initialData.selected_logo_id && initialData.logos) {
            const selectedLogo = initialData.logos.find(logo => logo.id === initialData.selected_logo_id);
            if (selectedLogo) setSelectedLogo(selectedLogo);
          }
          if (initialData.selected_watermark_id && initialData.watermarks) {
            const selectedWatermark = initialData.watermarks.find(watermark => watermark.id === initialData.selected_watermark_id);
            if (selectedWatermark) setSelectedWatermark(selectedWatermark);
          }
        }
      }, [initialData]);

      const handleSubmit = (e) => {
        e.preventDefault();
        console.log('Submitting brand kit:', formData);
        onSubmit(formData);
      };

      const handleLogoUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;
        setUploadingLogos(true);
        try {
          const uploadPromises = files.map(file => assetsApi.uploadAsset(file, 'logo'));
          const results = await Promise.all(uploadPromises);
          const newLogos = results.map((result, index) => ({
            id: result.id,
            name: files[index].name,
            size: files[index].size,
            type: files[index].type,
            dropbox_path: result.dropbox_path,
            created_at: new Date().toISOString()
          }));
          setFormData(prev => ({ ...prev, logos: [...prev.logos, ...newLogos] }));
        } catch (error) {
          console.error('Logo upload failed:', error);
          showError(`Logo upload failed: ${error.message}`);
        } finally {
          setUploadingLogos(false);
          e.target.value = '';
        }
      };

      const handleWatermarkUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;
        setUploadingWatermarks(true);
        try {
          const uploadPromises = files.map(file => assetsApi.uploadAsset(file, 'watermark'));
          const results = await Promise.all(uploadPromises);
          const newWatermarks = results.map((result, index) => ({
            id: result.id,
            name: files[index].name,
            size: files[index].size,
            type: files[index].type,
            dropbox_path: result.dropbox_path,
            created_at: new Date().toISOString()
          }));
          setFormData(prev => ({ ...prev, watermarks: [...prev.watermarks, ...newWatermarks] }));
        } catch (error) {
          console.error('Watermark upload failed:', error);
          showError(`Watermark upload failed: ${error.message}`);
        } finally {
          setUploadingWatermarks(false);
          e.target.value = '';
        }
      };

      const removeLogo = (index) => {
        setFormData(prev => ({ ...prev, logos: prev.logos.filter((_, i) => i !== index) }));
      };

      const removeWatermark = (index) => {
        setFormData(prev => ({ ...prev, watermarks: prev.watermarks.filter((_, i) => i !== index) }));
      };

      const handleWatermarkSettingsChange = (setting, value) => {
        setFormData(prev => ({
          ...prev,
          watermark_settings: { ...prev.watermark_settings, [setting]: value }
        }));
      };

      useEffect(() => {
        setFormData(prev => ({
          ...prev,
          selected_logo_id: selectedLogo?.id || null,
          selected_watermark_id: selectedWatermark?.id || null
        }));
      }, [selectedLogo, selectedWatermark]);

      return (
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              {initialData ? 'Edit Brand Kit' : 'Create New Brand Kit'}
            </h3>
            <button type="button" onClick={onCancel} className="p-2 text-gray-400 hover:text-gray-600">
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2"> Brand Kit Name </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter brand kit name"
              required
            />
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <SafeIcon icon={FiLock} className="w-4 h-4 mr-2 text-gray-400" /> Locked Brand Colors
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(LOCKED_COLORS).map(([colorType, color]) => (
                <div key={colorType}>
                  <label className="block text-sm text-gray-600 mb-1 capitalize">
                    {colorType === 'primary' ? 'Primary' : colorType === 'accent' ? 'Accent' : colorType === 'background' ? 'Background' : colorType === 'secondary' ? 'Secondary' : 'Highlight'} Color
                  </label>
                  <div className="flex items-center space-x-2">
                    <div className="w-10 h-10 rounded border-2 border-gray-300" style={{ backgroundColor: color }} />
                    <input type="text" value={color} disabled className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-gray-500" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <SafeIcon icon={FiLock} className="w-4 h-4 mr-2 text-gray-400" /> Locked Typography
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(LOCKED_FONTS).map(([fontType, font]) => (
                <div key={fontType}>
                  <label className="block text-sm text-gray-600 mb-1 capitalize">
                    {fontType === 'titles' ? 'Titles' : fontType === 'body' ? 'Body' : fontType === 'subtitle' ? 'Subtitle' : fontType === 'heading' ? 'Heading' : 'Highlight'}
                  </label>
                  <input type="text" value={font} disabled className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded text-gray-500" />
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <SafeIcon icon={FiImage} className="w-4 h-4 mr-2" /> Logo Assets ({formData.logos.length})
            </h4>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
              <input type="file" multiple accept="image/*" onChange={handleLogoUpload} disabled={uploadingLogos} className="hidden" id="logo-upload" />
              <label htmlFor="logo-upload" className={`flex items-center justify-center w-full py-4 px-4 rounded-lg cursor-pointer transition-colors ${uploadingLogos ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}>
                {uploadingLogos ? (
                  <><SafeIcon icon={FiUpload} className="w-5 h-5 mr-2 animate-spin" /> Uploading logos...</>
                ) : (
                  <><SafeIcon icon={FiPlus} className="w-5 h-5 mr-2" /> Add Logos</>
                )}
              </label>
            </div>
            {formData.logos.length > 0 && (
              <div className="mt-4 space-y-2">
                {formData.logos.map((logo, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <SafeIcon icon={FiImage} className="w-4 h-4 text-gray-500 mr-3" />
                      <div>
                        <p className="font-medium text-gray-900">{logo.name}</p>
                        <p className="text-sm text-gray-500">{(logo.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                    </div>
                    <button type="button" onClick={() => removeLogo(index)} className="p-2 text-red-500 hover:text-red-700 transition-colors">
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <SafeIcon icon={FiImage} className="w-4 h-4 mr-2" /> Watermark Assets ({formData.watermarks.length})
            </h4>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
              <input type="file" multiple accept="image/*" onChange={handleWatermarkUpload} disabled={uploadingWatermarks} className="hidden" id="watermark-upload" />
              <label htmlFor="watermark-upload" className={`flex items-center justify-center w-full py-4 px-4 rounded-lg cursor-pointer transition-colors ${uploadingWatermarks ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}>
                {uploadingWatermarks ? (
                  <><SafeIcon icon={FiUpload} className="w-5 h-5 mr-2 animate-spin" /> Uploading watermarks...</>
                ) : (
                  <><SafeIcon icon={FiPlus} className="w-5 h-5 mr-2" /> Add Watermarks</>
                )}
              </label>
            </div>
            {formData.watermarks.length > 0 && (
              <div className="mt-4 space-y-2">
                {formData.watermarks.map((watermark, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <SafeIcon icon={FiImage} className="w-4 h-4 text-gray-500 mr-3" />
                      <div>
                        <p className="font-medium text-gray-900">{watermark.name}</p>
                        <p className="text-sm text-gray-500">{(watermark.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                    </div>
                    <button type="button" onClick={() => removeWatermark(index)} className="p-2 text-red-500 hover:text-red-700 transition-colors">
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {(formData.logos.length > 0 || formData.watermarks.length > 0) && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Select Default Assets</h4>
              <div className="space-y-4">
                {formData.logos.length > 0 && (
                  <AssetSelector assets={formData.logos} type="logo" selectedId={selectedLogo?.id} onSelect={setSelectedLogo} title="Select Primary Logo" />
                )}
                {formData.watermarks.length > 0 && (
                  <AssetSelector assets={formData.watermarks} type="watermark" selectedId={selectedWatermark?.id} onSelect={setSelectedWatermark} title="Select Default Watermark" />
                )}
              </div>
            </div>
          )}

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Watermark Settings</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Opacity</label>
                <input type="range" min="0" max="1" step="0.1" value={formData.watermark_settings.opacity} onChange={(e) => handleWatermarkSettingsChange('opacity', parseFloat(e.target.value))} className="w-full" />
                <span className="text-sm text-gray-500">{formData.watermark_settings.opacity}</span>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Position</label>
                <select value={formData.watermark_settings.position} onChange={(e) => handleWatermarkSettingsChange('position', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="top-left">Top Left</option>
                  <option value="top-right">Top Right</option>
                  <option value="center">Center</option>
                  <option value="bottom-left">Bottom Left</option>
                  <option value="bottom-right">Bottom Right</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Size</label>
                <select value={formData.watermark_settings.size} onChange={(e) => handleWatermarkSettingsChange('size', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                </select>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"> Cancel </button>
            <motion.button type="submit" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
              {initialData ? 'Update' : 'Create'} Brand Kit
            </motion.button>
          </div>
        </form>
      );
    }